import { type TrialOffer } from '@/api/gen/_model/trialOffer';
import { type UserVoucherRes } from '@/api/gen/_model/userVoucherRes';
import { isPhoneTrialUpsellEligible } from '@/shared/features/phone-verification/utils';
import { type PlanType } from '@/types';

import { hasEnoughPlan } from './hasEnoughPlan';

/** 유료 기능이 요구하는 플랜. FREE 기능은 애초에 페이월을 타지 않는다. */
export type RequiredPlan = Exclude<PlanType, 'FREE'>;

/**
 * 페이월 진입 시점의 이용권·체험 상태.
 * 이 타입에 담기는 값은 전부 호출자가 조회해서 넘긴다 — 판정 자체는 순수하게 유지한다.
 */
export interface PremiumAccessState {
  requiredPlan: RequiredPlan;
  trialOffer?: TrialOffer;
  /** 미사용(UNUSED) 이용권 목록. 조회 실패 시에는 판정을 돌리지 않는다. */
  vouchers: UserVoucherRes[];
  /**
   * 계정 전체에 이용권 사용이 열려 있는지(서버 판정).
   * 플랜 적용이 예정된 FREE 사용자처럼, 개별 이용권이 멀쩡해도 활성화가 막히는 경우가 있다.
   */
  canUseVouchers: boolean;
}

export type PremiumAccessResult =
  /** 바로 사용할 수 있는 이용권이 있다. 결제를 권하지 않는다. */
  | { type: 'usable-voucher'; voucher: UserVoucherRes }
  /**
   * 휴대폰 인증으로 무료 체험을 받을 수 있다. 업셀링과 함께 인증 배너를 노출한다.
   * `requiredPlan` 은 기능이 요구하는 플랜(가격 카드 기본 선택용)이다.
   * 체험으로 지급되는 이용권은 이와 무관하게 항상 PRO 다.
   */
  | { type: 'phone-trial'; requiredPlan: RequiredPlan }
  /** 남은 길은 결제뿐이다. */
  | { type: 'pricing'; requiredPlan: RequiredPlan };

/** 판정과 트래킹이 공유하는, 요청 기능에 바로 쓸 수 있는 이용권 목록. */
const getUsableVouchers = ({
  requiredPlan,
  vouchers,
  canUseVouchers,
}: PremiumAccessState) => {
  if (!canUseVouchers) return [];

  return vouchers.filter(
    voucher =>
      voucher.canUseNow && hasEnoughPlan(voucher.planType, requiredPlan),
  );
};

/** 요청 기능에 바로 쓸 수 있는 이용권 수. 트래킹과 판정이 같은 기준을 사용한다. */
export const getUsableVoucherCount = (state: PremiumAccessState) =>
  getUsableVouchers(state).length;

/**
 * 유료 기능 진입을 시도했을 때 무엇을 보여줄지 결정한다.
 *
 * 실제 기능 접근 권한은 서버가 계속 판정한다. 이 함수는 서버가 막은 뒤(또는 막기 전)
 * **어떤 UI 를 보여줄지**만 정하는 UX 정책이며, 권한 판정을 대체하지 않는다.
 *
 * 우선순위가 곧 계약이다:
 *   1. 사용 가능한 이용권 → 이용권 안내 (보유한 걸 두고 결제를 권하지 않는다)
 *   2. 무료 체험 대상    → 업셀링 + 인증 배너
 *   3. 그 외             → 업셀링
 *
 * ⚠️ "이미 충분한 플랜인가"는 여기서 판단하지 않는다.
 * 호출부는 전부 접근이 막혔다고 판단한 뒤에 부른다(가드는 canAccessFeature,
 * MapTemplateSubmitAction 은 hasEnoughPlanType). 그 판정은 지역별 기능 세트
 * (PLAN_FEATURES[region])를 보는데, 여기서 지역을 모르는 플랜 등급 비교로
 * 다시 판단하면 서로 어긋난다.
 *
 * 실제로 어긋나는 조합이 있다 — AI_TUTOR 는 GLOBAL PRO 세트에 없어서
 * 해외 PRO 사용자는 canAccessFeature=false(막힘)인데 등급 비교로는 충족이다.
 * 여기서 "이미 충분함"으로 접으면 버튼을 눌러도 아무 일도 일어나지 않는다.
 */
export const resolvePremiumAccess = (
  state: PremiumAccessState,
): PremiumAccessResult => {
  const { requiredPlan, trialOffer } = state;

  // 요구 등급에 미달하는 이용권은 없는 것으로 취급한다.
  // 예: BASIC 이용권 보유자가 PRO 기능을 누르면 이용권을 언급하지 않고 PRO 업셀링으로 간다.
  //
  // canUseVouchers 가 false 면 개별 이용권이 멀쩡해도 활성화가 실패한다.
  // 실패할 걸 권하느니 결제 안내로 보낸다.
  const usableVoucher = getUsableVouchers(state)[0];

  if (usableVoucher) {
    return { type: 'usable-voucher', voucher: usableVoucher };
  }

  if (isPhoneTrialUpsellEligible(trialOffer)) {
    // 체험 지급분은 PRO 고정이지만, 가격 카드는 기능이 요구하는 플랜을 기준으로 둔다.
    // 여기서 PRO 로 덮어쓰면 BASIC 기능에서도 PRO 카드가 선택돼 버린다.
    return { type: 'phone-trial', requiredPlan };
  }

  return { type: 'pricing', requiredPlan };
};
